import { useCallback, useEffect, useRef } from "react";

const BOTTOM_THRESHOLD = 150;
const PROGRAMMATIC_SCROLL_TIMEOUT = 1200;

export const useAutoScroll = (
  scrollContainerRef: React.RefObject<HTMLElement | null>
) => {
  const isAutoScrollEnabledRef = useRef(true);
  const isProgrammaticScroll = useRef(false);
  const programmaticTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastScrollHeightRef = useRef(0);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // If scrollHeight grew, the scroll was caused by new content being added,
      // not by user action — don't disable auto-scroll
      if (scrollHeight > lastScrollHeightRef.current) {
        lastScrollHeightRef.current = scrollHeight;
        return;
      }
      lastScrollHeightRef.current = scrollHeight;

      isAutoScrollEnabledRef.current = distanceFromBottom <= BOTTOM_THRESHOLD;
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef]);

  const scrollToElement = useCallback(
    (
      element: HTMLElement,
      options?: ScrollIntoViewOptions,
      { force = false }: { force?: boolean } = {}
    ) => {
      if (!force && !isAutoScrollEnabledRef.current) return;

      isProgrammaticScroll.current = true;
      clearTimeout(programmaticTimeoutRef.current);

      // Sync scrollHeight tracking before programmatic scroll
      const container = scrollContainerRef.current;
      if (container) {
        lastScrollHeightRef.current = container.scrollHeight;
      }

      element.scrollIntoView(options);

      // If this was a forced (user-initiated) scroll, re-enable auto-scroll
      if (force) {
        isAutoScrollEnabledRef.current = true;
      }

      programmaticTimeoutRef.current = setTimeout(() => {
        isProgrammaticScroll.current = false;
        // Re-sync scrollHeight after programmatic scroll settles
        if (container) {
          lastScrollHeightRef.current = container.scrollHeight;
        }
      }, PROGRAMMATIC_SCROLL_TIMEOUT);
    },
    [scrollContainerRef]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearTimeout(programmaticTimeoutRef.current);
  }, []);

  return { scrollToElement };
};
